import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  useCanManageTargets,
  useCanReadForecasting,
  useCanReadTargets,
} from "../../../auth/hooks";

export type WorkspaceTab =
  "plan" | "pipeline" | "movement" | "targets" | "reviews";

interface WorkspaceNavProps {
  active: WorkspaceTab;
}

interface NavItem {
  key: WorkspaceTab;
  label: string;
  to: string;
  managerOnly?: boolean;
  targetsReadOnly?: boolean;
  forecastingOnly?: boolean;
}

/**
 * Shared tab strip across the forecast workspaces (Plan, Targets,
 * Review). Visible on every page in the workspace so the user can
 * switch between them at any drill level. My Pipeline is the rep's weekly
 * maintenance pass over their own book; Plan is the committed forecast grid.
 * Targets shows for every role that holds targets:read (managers edit drafts,
 * sales reps read the published plan); Movement stays manager-only. Roles
 * without targets access never see the tab. Plan and Review both read
 * endpoints gated on FORECASTING_ALL, which a CSR does not hold — without
 * the filter a CSR sees two tabs that 403, so they are gated the same way.
 * My Pipeline is deliberately ungated: a CSR who owns a portfolio works
 * their book here like anyone else. (The old Buckets/Patterns/Calibration tabs
 * were retired: calibration relocated into the plan drill's
 * Calibration card; buckets/patterns had no active users.)
 */
export function WorkspaceNav({ active }: WorkspaceNavProps) {
  const canManageTargets = useCanManageTargets();
  const canReadTargets = useCanReadTargets();
  const canReadForecasting = useCanReadForecasting();
  const items: NavItem[] = [
    { key: "plan", label: "Plan", to: paths.plan(), forecastingOnly: true },
    { key: "pipeline", label: "My Pipeline", to: paths.myPipeline() },
    {
      key: "movement",
      label: "Movement",
      to: paths.forecastMovement(),
      managerOnly: true,
    },
    {
      key: "targets",
      label: "Targets",
      to: paths.planTargets(),
      targetsReadOnly: true,
    },
    {
      key: "reviews",
      label: "Review",
      to: paths.planCycleReviewIndex(),
      forecastingOnly: true,
    },
  ];

  const visible = items.filter(
    (i) =>
      (!i.managerOnly || canManageTargets) &&
      (!i.targetsReadOnly || canReadTargets) &&
      (!i.forecastingOnly || canReadForecasting),
  );

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border pb-2">
      {visible.map((i) => {
        const isActive = i.key === active;
        return (
          <Link
            key={i.key}
            to={i.to}
            className={`px-3 py-1.5 text-sm rounded-md ${
              isActive
                ? "bg-primary/10 text-primary font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
            }`}
          >
            {i.label}
          </Link>
        );
      })}
    </div>
  );
}
